import { inject, Injectable, signal } from '@angular/core'
import { firstValueFrom } from 'rxjs'

import { Sprint } from '@core/interfaces/sprints.interface'
import { SprintsService } from '@core/services/sprints.service'
import { ErrorService } from '@core/services/error/error.service'
import { User } from '@core/interfaces/participant.interface'

@Injectable({
  providedIn: 'root'
})
export class SprintsStore {
  #sprintsService = inject(SprintsService)
  #errorService = inject(ErrorService)

  #sprints = signal<Sprint[]>([])
  sprints = this.#sprints.asReadonly()

  /**
   * Carga los sprints para un usuario específico
   *
   * @param {User} user - Usuario para el que cargar los sprints
   * @param {number} page - Número de página para la paginación (por defecto: 1)
   * @returns {Promise<void>}
   */
  async loadSprints(user: User, page = 1) {
    const existing = this.sprints()
    if (existing.length > 0) return existing

    try {
      const sprints$ = this.#sprintsService.fetchSprints(user, page)
      const sprints = await firstValueFrom(sprints$)

      this.setSprints(sprints)
      return sprints
    } catch (error) {
      this.#errorService.handleError(error)
      return []
    }
  }

  /**
   * Establece los sprints en el store, asegurando que no haya duplicados
   * utilizando un mapa con claves únicas por combinación de usuario y sprint ID
   *
   * @param {Sprint[]} sprints - Array de sprints a almacenar
   * @private
   */
  private setSprints(sprints: Sprint[]) {
    const existing = this.sprints()

    const sprintMap = new Map<string, Sprint>()

    existing.forEach(sprint => {
      sprintMap.set(`${sprint.userUid}-${sprint.id}`, sprint)
    })

    sprints.forEach(sprint => {
      sprintMap.set(`${sprint.userUid}-${sprint.id}`, sprint)
    })

    this.#sprints.set(Array.from(sprintMap.values()))
  }
}
